<script>
import { SHIPMENT_STATUSES } from '../../types/shipment'
import EcPagination from '../ui/EcPagination.vue'
import ShipmentStatusBadge from './ShipmentStatusBadge.vue'
import ShipmentItem from './ShipmentItem.vue'

export default {
  name: 'ShipmentList',
  components: { EcPagination, ShipmentStatusBadge, ShipmentItem },
  props: {
    shipments: { type: Array, required: true },
  },
  data() {
    return {
      search: '',
      statusFilter: '',
      perPage: 10,
      currentPage: 1,
      debouncedSearch: '',
      searchTimeout: null,
      expanded: {},
    }
  },
  computed: {
    t() {
      return this.$ecTranslate || ((k) => k)
    },
    filteredShipments() {
      let items = [...this.shipments]

      if (this.debouncedSearch) {
        const q = this.debouncedSearch.toLowerCase()
        items = items.filter((s) =>
          (s.name ?? '').toLowerCase().includes(q) ||
          (s.tracking_number ?? '').toLowerCase().includes(q) ||
          (s.sender?.contact_name ?? '').toLowerCase().includes(q) ||
          (s.sender?.company_name ?? '').toLowerCase().includes(q) ||
          (s.recipient?.attention_to ?? '').toLowerCase().includes(q) ||
          (s.recipient?.company_name ?? '').toLowerCase().includes(q) ||
          String(s.id).includes(q),
        )
      }

      if (this.statusFilter) {
        items = items.filter((s) => s.status === this.statusFilter)
      }

      return items
    },
    totalItems() {
      return this.filteredShipments.length
    },
    lastPage() {
      return Math.max(1, Math.ceil(this.totalItems / this.perPage))
    },
    paginatedShipments() {
      const start = (this.currentPage - 1) * this.perPage
      return this.filteredShipments.slice(start, start + this.perPage)
    },
    SHIPMENT_STATUSES() {
      return SHIPMENT_STATUSES
    },
  },
  watch: {
    search(val) {
      if (this.searchTimeout) clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.debouncedSearch = val
        this.currentPage = 1
      }, 300)
    },
    shipments: {
      handler(list) {
        if (list.length && !Object.keys(this.expanded).length) {
          this.$set(this.expanded, list[0].id, true)
        }
      },
      immediate: true,
    },
    statusFilter() {
      this.currentPage = 1
    },
    perPage() {
      this.currentPage = 1
    },
  },
  methods: {
    toggle(id) {
      this.$set(this.expanded, id, !this.expanded[id])
    },
  },
}
</script>

<template>
  <div class="ec-shipment-list">
    <!-- Filters row -->
    <div class="ec-shipment-list__filters">
      <div class="ec-form-group" style="min-width: 80px; margin-bottom: 0">
        <select v-model="perPage" class="ec-select__field">
          <option :value="10">10</option>
          <option :value="25">25</option>
          <option :value="50">50</option>
        </select>
      </div>
      <div class="ec-form-group" style="min-width: 160px; margin-bottom: 0">
        <select v-model="statusFilter" class="ec-select__field">
          <option value="">{{ t('ELIXIO_CONNECT.ALL_STATUSES') }}</option>
          <option v-for="s in SHIPMENT_STATUSES" :key="s" :value="s">
            {{ t('ELIXIO_CONNECT.SHIPMENT_STATUS_' + s) }}
          </option>
        </select>
      </div>
      <div class="ec-form-group" style="flex: 1; min-width: 180px; margin-bottom: 0">
        <div class="ec-input ec-input--has-icon">
          <i class="ec-input__icon bi bi-search" />
          <input
            v-model="search"
            type="text"
            class="ec-input__field"
            :placeholder="t('ELIXIO_CONNECT.SEARCH') + '...'"
          />
        </div>
      </div>
    </div>

    <!-- Items -->
    <div v-if="paginatedShipments.length" class="ec-shipment-list__items">
      <div
        v-for="shipment in paginatedShipments"
        :key="shipment.id"
        class="ec-shipment-list__item"
      >
        <!-- Accordion header -->
        <button
          class="ec-shipment-list__header"
          :class="{ 'ec-shipment-list__header--open': expanded[shipment.id] }"
          @click="toggle(shipment.id)"
        >
          <span class="ec-shipment-list__title">
            <i class="bi bi-truck" />
            <span v-if="shipment.name">
              {{ shipment.name }}
              <span v-if="shipment.tracking_number" class="ec-text-muted">({{ shipment.tracking_number }})</span>
            </span>
            <span v-else-if="shipment.tracking_number">{{ shipment.tracking_number }}</span>
            <span v-else class="ec-text-muted">{{ t('ELIXIO_CONNECT.SHIPMENT') }} #{{ shipment.id }}</span>
          </span>

          <div class="ec-flex ec-items-center ec-gap-2">
            <ShipmentStatusBadge :status="shipment.status" />
            <i
              class="bi"
              :class="expanded[shipment.id] ? 'bi-chevron-up' : 'bi-chevron-down'"
              style="font-size: 0.75rem; opacity: 0.5"
            />
          </div>
        </button>

        <!-- Accordion body -->
        <div v-show="expanded[shipment.id]" class="ec-shipment-list__body">
          <ShipmentItem :shipment="shipment" @updated="$emit('updated')" />
        </div>
      </div>
    </div>

    <!-- Empty state -->
    <div v-else class="ec-text-center ec-text-muted" style="padding: 2rem 0">
      {{ t('ELIXIO_CONNECT.NO_SHIPMENTS') }}
    </div>

    <!-- Pagination -->
    <div v-if="totalItems > 0" class="ec-shipment-list__footer">
      <small class="ec-text-muted">
        {{ t('ELIXIO_CONNECT.DISPLAY_FROM_X_TO_X_OF_X_ENTRIES', { from: (currentPage - 1) * perPage + 1, to: Math.min(currentPage * perPage, totalItems), of: totalItems }) }}
      </small>
      <EcPagination
        :current-page="currentPage"
        :last-page="lastPage"
        @update:current-page="currentPage = $event"
      />
    </div>
  </div>
</template>

<style scoped>
.ec-shipment-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.ec-shipment-list__filters {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  align-items: end;
}

.ec-shipment-list__items {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.ec-shipment-list__item {
  border: 1px solid var(--ec-border-color, #e2e8f0);
  border-radius: 8px;
  overflow: visible;
}

.ec-shipment-list__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 0.75rem 1rem;
  background: var(--ec-bg-muted, #f8f9fa);
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font: inherit;
  text-align: left;
  transition: background 0.15s;
}

.ec-shipment-list__header:hover {
  background: var(--ec-bg-muted, #f1f5f9);
}

.ec-shipment-list__header--open {
  border-bottom: 1px solid var(--ec-border-color, #e2e8f0);
  border-radius: 8px 8px 0 0;
}

.ec-shipment-list__title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;
  font-size: 0.875rem;
}

.ec-shipment-list__body {
  padding: 1rem;
}

.ec-shipment-list__footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
</style>
